import React, {useEffect, useMemo, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import ProjectsListUI from '../view/ProjectsListUI';
import ProjectsStore from '../../store/ProjectsStore';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import {Project} from '../../../../data/models/Projects/Project';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import ModalFade, {
  useModalFade,
} from '../../../Microreels/List/components/ModalFade';
import ProjectFormModal from '../../components/ProjectFormModal';
import {projectsText} from '../../projectsText';

interface Props {
  projectsStore?: ProjectsStore;
  navigationStore?: NavigationStore;
}

const ProjectsListContainer = inject(
  'projectsStore',
  'navigationStore',
)(
  observer(({projectsStore, navigationStore}: Props) => {
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [projectToRename, setProjectToRename] = useState<Project>();
    const [projectToDelete, setProjectToDelete] = useState<Project>();
    const {organizationId} = useParams();
    const navigate = useNavigate();

    const deleteFade = useModalFade(!!projectToDelete);

    const canManage = (navigationStore?.userPermissions || []).includes(
      APP_PERMISSIONS.ORG_CREATE_COURSES,
    );

    useEffect(() => {
      if (organizationId) {
        projectsStore!.fetchProjects(organizationId);
      }
    }, [organizationId]);

    const projects = useMemo(() => {
      const query = search.trim().toLowerCase();
      return projectsStore!.projects.filter(
        project => !query || project.name.toLowerCase().includes(query),
      );
    }, [projectsStore!.projects, search]);

    const onOpen = (project: Project) => {
      navigate(`/manage/${organizationId}/projects/${project.id}`);
    };

    const onCreate = async (name: string) => {
      if (!organizationId) return;
      const projectId = await projectsStore!.createProject(
        organizationId,
        name,
      );
      if (projectId) {
        setShowCreate(false);
        navigate(`/manage/${organizationId}/projects/${projectId}`);
      }
    };

    const onRename = async (name: string) => {
      if (!organizationId || !projectToRename) return;
      const success = await projectsStore!.editProject(
        organizationId,
        projectToRename.id,
        name,
      );
      if (success) {
        setProjectToRename(undefined);
      }
    };

    const onConfirmDelete = async () => {
      if (!organizationId || !projectToDelete) return;
      const success = await projectsStore!.deleteProject(
        organizationId,
        projectToDelete.id,
      );
      if (success) {
        setProjectToDelete(undefined);
      }
    };

    return (
      <>
        <ProjectsListUI
          projects={projects}
          isLoading={projectsStore!.isLoading}
          canManage={canManage}
          search={search}
          setSearch={setSearch}
          onCreate={() => setShowCreate(true)}
          onOpen={onOpen}
          onRename={setProjectToRename}
          onDelete={setProjectToDelete}
        />
        {showCreate && (
          <ProjectFormModal
            isLoading={projectsStore!.isLoading}
            onSubmit={onCreate}
            onClose={() => setShowCreate(false)}
          />
        )}
        {projectToRename && (
          <ProjectFormModal
            project={projectToRename}
            isLoading={projectsStore!.isLoading}
            onSubmit={onRename}
            onClose={() => setProjectToRename(undefined)}
          />
        )}
        {deleteFade.rendered && (
          <ModalFade closing={deleteFade.closing}>
            <ConfirmationModal
              shown
              title={projectsText.deleteConfirm({
                coursesNumber: projectToDelete?.coursesNumber || 0,
                microreelsNumber: projectToDelete?.microreelsNumber || 0,
                postsNumber: projectToDelete?.postsNumber || 0,
              })}
              acceptText={projectsText.delete}
              onAccept={onConfirmDelete}
              onCancel={() => setProjectToDelete(undefined)}
            />
          </ModalFade>
        )}
      </>
    );
  }),
);

export default ProjectsListContainer;
